import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import { IotTourModalBodyProps } from './types';
import { ContentSection } from './styles';
import { renderMediaContent } from 'src/components/tag-popup/config';
import { IotContent } from 'src/components/tag-tooltip/Components/IotContent';
import { TourActionType } from 'src/sdk-components/VSObject';
import { DeviceManager } from 'src/utils';
import truncate from 'src/components/tag-popup/utils';
import { convertMDLink } from 'src/utils/convertMDLink';
import { GalleryButton } from 'src/components/gallery/styles';
import Icon from 'src/units/Icon';

export const IotModalBody: FC<IotTourModalBodyProps & { children?: any }> = ({
  showDescription,
  tag,
  providers,
  setSelectedIndex,
  selectedCameras,
  handleCameraSelect,
  unshownDevices,
  viewState,
  moveToTag,
  handleUnlock,
  cameras,
  pauseTourMusic,
  doors,
  removeCameraFromGrid,
  engines,
  things
}) => {
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [inFullScreen, setInFullScreen] = useState(false);
  const containerRef = useRef<any>();

  const handleReadMoreClick = () => {
    setDescriptionExpanded(true);
  };

  const handleReadLessClick = () => {
    setDescriptionExpanded(false);
  };

  const trimmedDescription = useMemo(() => {
    if (!tag?.description) return '';
    return truncate(tag.description, 650);
  }, [tag]);

  const toggleFullScreen = (ref) => {
    if (!document.fullscreenElement) {
      if (ref.current.requestFullscreen) {
        ref.current
          .requestFullscreen()
          .then(() => {
            setInFullScreen(true);
          })
          .catch((err) => {
            setInFullScreen(false);
            alert(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
          });
      }
    } else {
      if (document.exitFullscreen) {
        setInFullScreen(false);
        document.exitFullscreen();
      }
    }
  };
  const handleFullScreenExit = () => {
    if (!document.fullscreenElement) {
      setInFullScreen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullScreenExit);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenExit);
    };
  }, []);

  useEffect(() => {
    setDescriptionExpanded(showDescription);
  }, [showDescription]);

  return (
    <>
      {tag?.description && (
        <ContentSection>
          <Typography
            dangerouslySetInnerHTML={{
              __html: descriptionExpanded
                ? convertMDLink(tag.description)
                : convertMDLink(trimmedDescription)
            }}
            sx={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}
          />
          {!descriptionExpanded && trimmedDescription !== tag?.description && (
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 'bold',
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
              onClick={handleReadMoreClick}
            >{` Read more`}</Typography>
          )}
          {descriptionExpanded && trimmedDescription !== tag?.description && (
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 'bold',
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
              onClick={handleReadLessClick}
            >{` Read less`}</Typography>
          )}
        </ContentSection>
      )}
      <IotContent
        tag={tag}
        providers={providers}
        setSelectedIndex={setSelectedIndex}
        handleUnlock={handleUnlock}
        selectedCameras={selectedCameras}
        handleCameraSelect={handleCameraSelect}
        unshownDevices={unshownDevices}
        viewState={viewState}
        moveToTag={moveToTag}
        cameras={cameras}
        doors={doors}
        removeCameraFromGrid={removeCameraFromGrid}
        engines={engines}
        things={things}
      />
      {tag?.type !== TourActionType.Iframe &&
      (tag?.tagGalleries?.length || (tag?.videoUrl && tag?.videoUrl !== 'null')) ? (
        <ContentSection>
          {renderMediaContent(tag, inFullScreen, toggleFullScreen, pauseTourMusic)}
        </ContentSection>
      ) : null}
      {tag?.type === TourActionType.Iframe && (
        <ContentSection>
          <div
            ref={containerRef}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {inFullScreen ? (
              <GalleryButton
                style={{
                  position: 'fixed',
                  top: DeviceManager.isMobile() ? '14px' : `30px`,
                  right: DeviceManager.isMobile() ? '14px' : '30px',
                  background: 'rgba(0, 0, 0, 0.1)',
                  // @ts-ignore
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.2)'
                  },
                  // @ts-ignore
                  ' &:focus': {
                    backgroundColor: 'rgba(0, 0, 0, 0.2)'
                  }
                }}
                onClick={() => toggleFullScreen(containerRef)}
              >
                <Icon.Close
                  style={{ color: 'white', width: '100%', height: '100%' }}
                  fontSize="large"
                />
              </GalleryButton>
            ) : (
              <GalleryButton
                style={{
                  position: 'absolute',
                  bottom: `${20}px`,
                  right: '30px',
                  background: 'rgba(0, 0, 0, 0.1)',
                  // @ts-ignore
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.2)'
                  },
                  // @ts-ignore
                  ' &:focus': {
                    backgroundColor: 'rgba(0, 0, 0, 0.2)'
                  }
                }}
                onClick={() => toggleFullScreen(containerRef)}
              >
                <Icon.FullScreen />
              </GalleryButton>
            )}
            <iframe
              style={{
                width: inFullScreen ? '100%' : DeviceManager.isMobile() ? 'auto' : '960px',
                height: inFullScreen ? '100%' : DeviceManager.isMobile() ? 'auto' : '540px'
              }}
              src={
                DeviceManager.isMobile() && tag.url?.endsWith('.pdf')
                  ? `https://docs.google.com/viewerng/viewer?url=${tag.url}&embedded=true`
                  : tag.url
              }
            ></iframe>
          </div>
        </ContentSection>
      )}
    </>
  );
};
